// Chromabow Effect Implementation
// Features: Magnificent crystal prismatic bow materializes, draws an energy arrow,
// fires with a rainbow ribbon trail, and explodes into colorful shards.

const CONFIG = {
    scale: 0.8,
    materializeDuration: 750, // 500 * 1.5
    drawDuration: 1200,      // 800 * 1.5
    fireDuration: 375,       // 250 * 1.5
    impactDuration: 1200,    // 800 * 1.5
    trailLength: 20,
};

const spriteCache = new Map<string, HTMLCanvasElement>();

function getBowChassisSprite(): HTMLCanvasElement {
    const key = 'chromabowChassis';
    let sprite = spriteCache.get(key);
    if (!sprite) {
        sprite = document.createElement('canvas');
        sprite.width = 300;
        sprite.height = 400;
        const ctx = sprite.getContext('2d');
        if (ctx) {
            const cx = 150;
            const cy = 200;

            // We are building an ornate, elven-style recurve bow.
            // Symmetrical up/down. We'll define a draw function for one half and mirror it.

            ctx.lineJoin = 'round';
            ctx.lineCap = 'round';

            const drawElvenLimb = (isUpper: boolean) => {
                ctx.save();
                if (!isUpper) {
                    ctx.translate(cx, cy);
                    ctx.scale(1, -1);
                    ctx.translate(-cx, -cy);
                }

                // --- TEAL INNER CORE (Glowing Magic) ---
                ctx.beginPath();
                ctx.moveTo(cx - 5, cy - 30);
                ctx.bezierCurveTo(cx + 40, cy - 70, cx + 50, cy - 120, cx - 15, cy - 170);
                ctx.bezierCurveTo(cx + 20, cy - 130, cx + 20, cy - 80, cx - 15, cy - 30);
                ctx.closePath();
                const tealGrad = ctx.createLinearGradient(cx - 20, cy - 170, cx + 50, cy - 30);
                tealGrad.addColorStop(0, '#00ffff');
                tealGrad.addColorStop(0.5, '#0088ff');
                tealGrad.addColorStop(1, '#00d4ff');
                ctx.fillStyle = tealGrad;
                ctx.shadowBlur = 15;
                ctx.shadowColor = '#00ffff';
                ctx.fill();
                ctx.shadowBlur = 0; // reset

                // Inner bright teal highlight
                ctx.beginPath();
                ctx.moveTo(cx + 10, cy - 50);
                ctx.bezierCurveTo(cx + 35, cy - 80, cx + 35, cy - 120, cx - 10, cy - 160);
                ctx.strokeStyle = '#e0ffff';
                ctx.lineWidth = 3;
                ctx.stroke();

                // --- GREEN LEAF / FEATHER ACCENTS ---
                const drawLeaf = (lx: number, ly: number, scale: number, rot: number) => {
                    ctx.save();
                    ctx.translate(lx, ly);
                    ctx.rotate(rot);
                    ctx.scale(scale, scale);
                    ctx.beginPath();
                    ctx.moveTo(0, 0);
                    ctx.bezierCurveTo(-20, -10, -30, -30, 0, -50);
                    ctx.bezierCurveTo(10, -30, 15, -10, 0, 0);
                    const leafGrad = ctx.createLinearGradient(0, -50, 0, 0);
                    leafGrad.addColorStop(0, '#00ff00');
                    leafGrad.addColorStop(1, '#006600');
                    ctx.fillStyle = leafGrad;
                    ctx.fill();
                    // Spine
                    ctx.beginPath();
                    ctx.moveTo(0, 0);
                    ctx.bezierCurveTo(-5, -20, -5, -40, 0, -50);
                    ctx.strokeStyle = '#aaffaa';
                    ctx.lineWidth = 1;
                    ctx.stroke();
                    ctx.restore();
                };
                // Leaf cluster near grip
                drawLeaf(cx - 15, cy - 40, 0.8, -Math.PI / 4);
                drawLeaf(cx - 5, cy - 45, 1.2, -Math.PI / 6);
                drawLeaf(cx + 10, cy - 35, 0.9, 0);

                // Leaf cluster near tip
                drawLeaf(cx - 10, cy - 140, 0.7, -Math.PI / 3);
                drawLeaf(cx, cy - 130, 0.6, -Math.PI / 6);


                // --- ORNATE GOLD CHASSIS ---
                // Outer gold spine
                ctx.beginPath();
                ctx.moveTo(cx - 20, cy - 20); // base at grip
                ctx.bezierCurveTo(cx - 30, cy - 40, cx - 10, cy - 80, cx + 25, cy - 100); // inner hook
                ctx.bezierCurveTo(cx + 50, cy - 120, cx + 20, cy - 160, cx - 20, cy - 180); // upper tip
                ctx.bezierCurveTo(cx - 40, cy - 150, cx - 5, cy - 110, cx - 5, cy - 90);
                ctx.bezierCurveTo(cx - 5, cy - 60, cx - 40, cy - 30, cx - 20, cy - 20);
                ctx.closePath();

                // Inner gold wrap
                ctx.moveTo(cx + 10, cy - 20);
                ctx.bezierCurveTo(cx + 40, cy - 40, cx + 60, cy - 80, cx + 45, cy - 130);
                ctx.bezierCurveTo(cx + 50, cy - 110, cx + 45, cy - 60, cx + 10, cy - 20);

                const goldGrad = ctx.createLinearGradient(cx - 40, cy - 180, cx + 60, cy - 20);
                goldGrad.addColorStop(0, '#fff5b0'); // highlight
                goldGrad.addColorStop(0.3, '#d4af37'); // metallic gold
                goldGrad.addColorStop(0.7, '#aa7700'); // dark gold
                goldGrad.addColorStop(1, '#ffdf00'); // bright base
                ctx.fillStyle = goldGrad;
                ctx.shadowBlur = 10;
                ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
                ctx.fill();
                ctx.shadowBlur = 0;

                // Gold edge highlights
                ctx.beginPath();
                ctx.moveTo(cx - 20, cy - 180);
                ctx.bezierCurveTo(cx + 20, cy - 160, cx + 50, cy - 120, cx + 25, cy - 100);
                ctx.strokeStyle = '#ffffee';
                ctx.lineWidth = 1.5;
                ctx.stroke();

                ctx.restore();
            };

            // Draw both halves
            drawElvenLimb(true);
            drawElvenLimb(false);

            // --- BROWN LEATHER GRIP ---
            ctx.save();
            ctx.shadowBlur = 5;
            ctx.shadowColor = 'black';

            // Wrap shape
            const gw = 18, gh = 50;
            const gripGrad = ctx.createLinearGradient(cx - gw, 0, cx + gw, 0);
            gripGrad.addColorStop(0, '#3e2723'); // dark brown
            gripGrad.addColorStop(0.5, '#6d4c41'); // lighter brown
            gripGrad.addColorStop(1, '#3e2723');

            ctx.beginPath();
            ctx.moveTo(cx - gw, cy - gh / 2);
            ctx.quadraticCurveTo(cx - gw + 5, cy, cx - gw, cy + gh / 2);
            ctx.lineTo(cx + gw, cy + gh / 2);
            ctx.quadraticCurveTo(cx + gw - 5, cy, cx + gw, cy - gh / 2);
            ctx.closePath();
            ctx.fillStyle = gripGrad;
            ctx.fill();

            // Leather wrapping lines
            ctx.strokeStyle = '#271510';
            ctx.lineWidth = 3;
            for (let i = -20; i <= 20; i += 8) {
                ctx.beginPath();
                ctx.moveTo(cx - gw + (i % 6 > 3 ? 2 : 0), cy + i); // slight zigzag
                ctx.lineTo(cx + gw - (i % 6 > 3 ? 0 : 2), cy + i + 5);
                ctx.stroke();
            }

            // Grip gold end-caps
            ctx.fillStyle = '#d4af37';
            ctx.fillRect(cx - gw - 2, cy - gh / 2 - 4, gw * 2 + 4, 8);
            ctx.fillRect(cx - gw - 2, cy + gh / 2 - 4, gw * 2 + 4, 8);

            // Gem in the center of the grip
            ctx.beginPath();
            ctx.arc(cx, cy, 6, 0, Math.PI * 2);
            ctx.fillStyle = '#00ffff';
            ctx.shadowBlur = 10;
            ctx.shadowColor = '#00ffff';
            ctx.fill();
            ctx.beginPath();
            ctx.arc(cx - 2, cy - 2, 2, 0, Math.PI * 2);
            ctx.fillStyle = '#ffffff';
            ctx.fill();

            ctx.restore();
        }
        spriteCache.set(key, sprite);
    }
    return sprite;
}

// Particle Classes
class LightMote {
    x: number; y: number; vx: number; vy: number; life: number; maxLife: number; color: string; size: number;
    constructor(x: number, y: number, color: string) {
        this.x = x; this.y = y;
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 2 + 0.5;
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed;
        this.maxLife = 30 + Math.random() * 30;
        this.life = this.maxLife;
        this.color = color;
        this.size = 1 + Math.random() * 2;
    }
    update() {
        this.x += this.vx; this.y += this.vy;
        this.life--;
    }
    draw(ctx: CanvasRenderingContext2D) {
        if (this.life <= 0) return;
        ctx.globalAlpha = this.life / this.maxLife;
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
    }
}

class PrismaticShard {
    x: number; y: number; vx: number; vy: number; life: number; maxLife: number; hue: number; rot: number; rotSpeed: number; size: number;
    constructor(x: number, y: number) {
        this.x = x; this.y = y;
        const angle = Math.random() * Math.PI * 2;
        const speed = 4 + Math.random() * 10;
        this.vx = Math.cos(angle) * speed;
        this.vy = Math.sin(angle) * speed;
        this.maxLife = 40 + Math.random() * 30;
        this.life = this.maxLife;
        this.hue = Math.random() * 360;
        this.rot = Math.random() * Math.PI * 2;
        this.rotSpeed = (Math.random() - 0.5) * 0.5;
        this.size = 3 + Math.random() * 8;
    }
    update() {
        this.x += this.vx; this.y += this.vy;
        this.vx *= 0.94; // friction
        this.vy *= 0.94;
        this.rot += this.rotSpeed;
        this.life--;
    }
    draw(ctx: CanvasRenderingContext2D) {
        if (this.life <= 0) return;
        const a = this.life / this.maxLife;
        ctx.globalAlpha = a;
        ctx.save();
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rot);

        ctx.fillStyle = `hsl(${this.hue}, 100%, 60%)`;
        ctx.shadowBlur = 10;
        ctx.shadowColor = ctx.fillStyle;

        ctx.beginPath();
        ctx.moveTo(0, -this.size);
        ctx.lineTo(this.size * 0.5, 0);
        ctx.lineTo(0, this.size * 1.5);
        ctx.lineTo(-this.size * 0.5, 0);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = 'white';
        ctx.beginPath();
        ctx.moveTo(0, -this.size * 0.5);
        ctx.lineTo(this.size * 0.2, 0);
        ctx.lineTo(0, this.size * 0.5);
        ctx.lineTo(-this.size * 0.2, 0);
        ctx.closePath();
        ctx.fill();

        ctx.restore();
    }
}

class RainbowRibbonNode {
    x: number; y: number; age: number; maxAge: number;
    constructor(x: number, y: number) {
        this.x = x; this.y = y; this.age = 0; this.maxAge = 20;
    }
}

export type ChromabowEffect = {
    kind: 'chromabow'
    t: number
    sx: number; sy: number
    tx: number; ty: number
    data?: {
        phase: 'MATERIALIZE' | 'DRAW' | 'FIRE' | 'IMPACT'
        motes: LightMote[]
        shards: PrismaticShard[]
        trailNodeFrames: RainbowRibbonNode[][] // Store history of positions for thick ribbon
        arrowProgress: number // 0 to 1 along the path
        bowRot: number
        distance: number
        angle: number
    }
}

export const createChromabowEffect = (sx: number, sy: number, tx: number, ty: number): ChromabowEffect => ({
    kind: 'chromabow', t: 0, sx, sy, tx, ty
})

export const renderChromabowEffect = (
    eff: ChromabowEffect,
    ctx: CanvasRenderingContext2D
): boolean => {
    if (!eff.data) {
        const dx = eff.tx - eff.sx;
        const dy = eff.ty - eff.sy;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const angle = Math.atan2(dy, dx);

        eff.data = {
            phase: 'MATERIALIZE',
            motes: [],
            shards: [],
            trailNodeFrames: [],
            arrowProgress: 0,
            bowRot: angle, // Base aim angle
            distance,
            angle
        };
    }

    const d = eff.data;

    // Determine overall phase and local time
    if (eff.t < CONFIG.materializeDuration) d.phase = 'MATERIALIZE';
    else if (eff.t < CONFIG.materializeDuration + CONFIG.drawDuration) d.phase = 'DRAW';
    else if (eff.t < CONFIG.materializeDuration + CONFIG.drawDuration + CONFIG.fireDuration) d.phase = 'FIRE';
    else if (eff.t < CONFIG.materializeDuration + CONFIG.drawDuration + CONFIG.fireDuration + CONFIG.impactDuration) d.phase = 'IMPACT';
    else return false;

    // Phase local timers
    const tMat = Math.max(0, Math.min(1, eff.t / CONFIG.materializeDuration));
    const tDraw = Math.max(0, Math.min(1, (eff.t - CONFIG.materializeDuration) / CONFIG.drawDuration));
    const tFire = Math.max(0, Math.min(1, (eff.t - CONFIG.materializeDuration - CONFIG.drawDuration) / CONFIG.fireDuration));
    const tImpact = Math.max(0, Math.min(1, (eff.t - CONFIG.materializeDuration - CONFIG.drawDuration - CONFIG.fireDuration) / CONFIG.impactDuration));

    // Update Particles
    for (let i = d.motes.length - 1; i >= 0; i--) {
        d.motes[i].update();
        if (d.motes[i].life <= 0) {
            d.motes[i] = d.motes[d.motes.length - 1];
            d.motes.pop();
        }
    }
    for (let i = d.shards.length - 1; i >= 0; i--) {
        d.shards[i].update();
        if (d.shards[i].life <= 0) {
            d.shards[i] = d.shards[d.shards.length - 1];
            d.shards.pop();
        }
    }

    // Manage Trail Nodes
    if (d.trailNodeFrames.length > 0) {
        d.trailNodeFrames.forEach(frame => {
            frame.forEach(n => n.age++);
        });
        d.trailNodeFrames = d.trailNodeFrames.filter(frame => frame[0] && frame[0].age < frame[0].maxAge);
    }

    ctx.save();
    ctx.globalCompositeOperation = 'screen';

    // RENDER BOW
    if (d.phase === 'MATERIALIZE' || d.phase === 'DRAW' || d.phase === 'FIRE') {
        ctx.save();
        ctx.translate(eff.sx, eff.sy);

        let bowScale = CONFIG.scale;
        let bowAlpha = 1;
        let bowRecoil = 0;
        const maxBowPullback = 120; // Dramatic pullback of the entire bow

        // Draw distance (0 to 1)
        let pullAmount = 0;
        if (d.phase === 'DRAW') {
            pullAmount = 1 - Math.pow(1 - tDraw, 3);
            if (Math.random() < 0.3) {
                d.motes.push(new LightMote((Math.random() - 0.5) * 100, (Math.random() - 0.5) * 100, `hsl(${Math.random() * 360}, 100%, 70%)`));
            }
        }

        if (d.phase === 'MATERIALIZE') {
            const easeOutBounce = (x: number): number => {
                const n1 = 7.5625, d1 = 2.75;
                if (x < 1 / d1) return n1 * x * x;
                else if (x < 2 / d1) return n1 * (x -= 1.5 / d1) * x + 0.75;
                else if (x < 2.5 / d1) return n1 * (x -= 2.25 / d1) * x + 0.9375;
                else return n1 * (x -= 2.625 / d1) * x + 0.984375;
            };
            bowScale = CONFIG.scale * easeOutBounce(tMat);
            bowAlpha = tMat;
            ctx.rotate(d.angle + Math.PI * (1 - tMat)); // Spin into place
        } else {
            ctx.rotate(d.angle);
            // Shake during max draw
            if (d.phase === 'DRAW') {
                if (tDraw > 0.8) {
                    ctx.translate((Math.random() - 0.5) * 4, (Math.random() - 0.5) * 4);
                }
                bowRecoil = -pullAmount * maxBowPullback;
            }
            if (d.phase === 'FIRE') {
                // Powerful kickback recoil: kicks further back away from the target
                const kickback = Math.pow(tFire, 0.5) * 60; // Sharp backward kick
                bowRecoil = -maxBowPullback - kickback;
                bowAlpha = 1 - tFire; // Fade out quickly after firing
            }
        }

        ctx.translate(bowRecoil, 0); // Apply recoil backwards
        ctx.scale(bowScale, bowScale);
        ctx.globalAlpha = bowAlpha;

        const sprite = getBowChassisSprite();
        // Sprite center is cx=150, cy=200
        ctx.drawImage(sprite, -150, -200);

        const stringMidX = -150 - (pullAmount * 80); // String pulls BACK relative to the bow
        const stringMidY = -200; // Center grip Y is 0 in translation

        // String lines
        ctx.beginPath();
        ctx.moveTo(-20, -180); // Top tip
        ctx.lineTo(stringMidX + 150, 0);       // Nocking point
        ctx.lineTo(-20, 180);  // Bottom tip

        ctx.strokeStyle = `rgba(0, 255, 255, ${0.5 + pullAmount * 0.5})`;
        ctx.lineWidth = 3 + pullAmount * 2;
        ctx.shadowBlur = 10;
        ctx.shadowColor = '#00ffff';
        ctx.stroke();

        // The Arrow (While Draw)
        if (d.phase === 'DRAW') {
            const arrowLen = 150;
            const arrowTipX = stringMidX + 150 + arrowLen;

            ctx.beginPath();
            ctx.moveTo(stringMidX + 150, 0); // Nock
            ctx.lineTo(arrowTipX, 0); // Tip

            const grad = ctx.createLinearGradient(stringMidX + 150, 0, arrowTipX, 0);
            grad.addColorStop(0, 'rgba(255,255,255,0)');
            grad.addColorStop(0.5, 'rgba(0,255,255,1)');
            grad.addColorStop(1, 'rgba(255,255,255,1)');
            ctx.strokeStyle = grad;
            ctx.lineWidth = 6;
            ctx.lineCap = 'round';
            ctx.shadowBlur = 20;
            ctx.shadowColor = '#ffffff';
            ctx.stroke();

            // Arrow head
            ctx.beginPath();
            ctx.moveTo(arrowTipX + 20, 0);
            ctx.lineTo(arrowTipX - 10, -15);
            ctx.lineTo(arrowTipX, 0);
            ctx.lineTo(arrowTipX - 10, 15);
            ctx.closePath();
            ctx.fillStyle = '#ffffff';
            ctx.fill();
        }

        // Draw local motes charging
        d.motes.forEach(m => m.draw(ctx));

        ctx.restore();
    }

    // RENDER FIRE (ARROW TRAIL)
    if (d.phase === 'FIRE') {
        const maxBowPullback = 120;
        const arrowProgress = tFire; // 0 to 1

        // Add a stunning parabolic arc
        const arcHeight = -15; // Barely noticeable arc
        const currentArc = Math.sin(arrowProgress * Math.PI) * arcHeight;

        // Calculate the base linear point
        // Arrow launches from the pulled back bow position
        const startX = eff.sx - Math.cos(d.angle) * maxBowPullback;
        const startY = eff.sy - Math.sin(d.angle) * maxBowPullback;
        const totalDist = d.distance + maxBowPullback;

        const baseX = startX + Math.cos(d.angle) * (totalDist * arrowProgress);
        const baseY = startY + Math.sin(d.angle) * (totalDist * arrowProgress);

        // Translate the point perpendicular to the angle to create the arc
        const currentX = baseX + Math.cos(d.angle - Math.PI / 2) * currentArc;
        const currentY = baseY + Math.sin(d.angle - Math.PI / 2) * currentArc;

        // Calculate tangent angle for the arrow rotation
        const nextProgress = Math.min(1, arrowProgress + 0.01);
        const nextArc = Math.sin(nextProgress * Math.PI) * arcHeight;
        const nextBaseX = startX + Math.cos(d.angle) * (totalDist * nextProgress);
        const nextBaseY = startY + Math.sin(d.angle) * (totalDist * nextProgress);
        const nextX = nextBaseX + Math.cos(d.angle - Math.PI / 2) * nextArc;
        const nextY = nextBaseY + Math.sin(d.angle - Math.PI / 2) * nextArc;

        const travelAngle = Math.atan2(nextY - currentY, nextX - currentX);

        // Record trail node frame (record two points for a thick ribbon)
        const perpAngle = travelAngle + Math.PI / 2;
        const width = 14; // Base width at head
        const frame = [
            new RainbowRibbonNode(currentX + Math.cos(perpAngle) * width, currentY + Math.sin(perpAngle) * width),
            new RainbowRibbonNode(currentX - Math.cos(perpAngle) * width, currentY - Math.sin(perpAngle) * width)
        ];
        d.trailNodeFrames.push(frame);

        // Spawn trailing sparks! (Golden glowing embers)
        if (Math.random() < 0.6) {
            d.motes.push(new LightMote(currentX + (Math.random() - 0.5) * 20, currentY + (Math.random() - 0.5) * 20, '#ffcc00'));
        }

        // Draw Majestic Golden Winged Arrow Tip
        ctx.save();
        ctx.translate(currentX, currentY);
        ctx.rotate(travelAngle);

        // --- Rainbow Ring Core ---
        ctx.beginPath();
        ctx.scale(0.5, 1); // oval shape
        ctx.arc(-10, 0, 16, 0, Math.PI * 2);
        ctx.setTransform(1, 0, 0, 1, currentX, currentY); // reset scale
        ctx.rotate(travelAngle); // re-apply rotation
        ctx.lineWidth = 4;

        // Ring gradient
        const ringGrad = ctx.createLinearGradient(-10, -16, -10, 16);
        ringGrad.addColorStop(0, '#ff0000');
        ringGrad.addColorStop(0.2, '#ffff00');
        ringGrad.addColorStop(0.4, '#00ff00');
        ringGrad.addColorStop(0.6, '#00ffff');
        ringGrad.addColorStop(0.8, '#ff00ff');
        ringGrad.addColorStop(1, '#ff0000');
        ctx.strokeStyle = ringGrad;
        ctx.stroke();

        ctx.shadowBlur = 15;
        ctx.shadowColor = '#00ffff';
        ctx.stroke();
        ctx.shadowBlur = 0;

        // --- Golden Projectile Head ---
        const tipGrad = ctx.createLinearGradient(-30, 0, 40, 0);
        tipGrad.addColorStop(0, '#d4af37'); // Gold
        tipGrad.addColorStop(1, '#fffacd'); // Bright gold tip
        ctx.fillStyle = tipGrad;

        // Main spearhead
        ctx.beginPath();
        ctx.moveTo(50, 0);       // sharp tip
        ctx.lineTo(20, -10);     // top flare
        ctx.lineTo(10, -5);      // neck
        ctx.lineTo(-10, -10);    // back
        ctx.lineTo(-40, 0);      // tail stem
        ctx.lineTo(-10, 10);     // back
        ctx.lineTo(10, 5);       // neck
        ctx.lineTo(20, 10);      // bottom flare
        ctx.closePath();
        ctx.fill();

        // Energy Wings (Red/Magenta)
        ctx.fillStyle = 'rgba(255, 0, 80, 0.9)';
        ctx.shadowBlur = 15;
        ctx.shadowColor = '#ff0050';

        const drawWing = (isUpper: boolean) => {
            ctx.save();
            if (!isUpper) ctx.scale(1, -1);
            ctx.beginPath();
            ctx.moveTo(-10, -10);
            ctx.bezierCurveTo(-30, -30, -40, -40, -20, -60); // swept back red wing
            ctx.bezierCurveTo(-10, -40, 5, -20, 20, -10);
            ctx.closePath();
            ctx.fill();
            ctx.restore();
        };

        drawWing(true);
        drawWing(false);

        ctx.restore();

        if (tFire >= 0.99 && d.shards.length === 0) {
            // Initiate impact explosion shards
            for (let i = 0; i < 30; i++) d.shards.push(new PrismaticShard(eff.tx, eff.ty));
        }
    }

    // RENDER RIBBON TRAIL
    if (d.trailNodeFrames.length > 2) {
        ctx.save();
        if (d.phase === 'IMPACT') ctx.globalAlpha = 1 - tImpact;
        else ctx.globalAlpha = 1;

        // We want 7 distinct ribbons that fan out towards the tail
        const colors = ['#ff0000', '#ff7f00', '#ffff00', '#00ff00', '#00ffff', '#4b0082', '#9400d3'];
        const bands = colors.length;

        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        for (let b = 0; b < bands; b++) {
            // Offset multiplier maps from -1 (bottom) to 1 (top)
            const mapOffset = (b / (bands - 1)) * 2 - 1;

            const path = new Path2D();
            for (let i = 0; i < d.trailNodeFrames.length; i++) {
                const frame = d.trailNodeFrames[i];
                // Progress from tail (0) to head (1)
                const pointProgress = i / (d.trailNodeFrames.length - 1);

                // Fan out effect: wider at tail, narrower at head
                const fanWidth = 1 + (1 - pointProgress) * 1.5;

                // Base width interpolation
                const midX = (frame[0].x + frame[1].x) / 2;
                const midY = (frame[0].y + frame[1].y) / 2;

                // Vector representing 1 side from center
                const dx = frame[0].x - midX;
                const dy = frame[0].y - midY;

                // Apply the offset multiplier and the fanning effect
                const px = midX + dx * mapOffset * fanWidth;
                const py = midY + dy * mapOffset * fanWidth;

                if (i === 0) path.moveTo(px, py);
                else path.lineTo(px, py);
            }

            // Draw custom gradient glow by layering strokes of increasing width and decreasing opacity
            // The user requested: 100% to 0% gradient, with a 1px solid core, and faster dropoff after 70%

            // Layer 4: Widest, faintest glow (0 to 30%)
            ctx.strokeStyle = colors[b];
            ctx.globalAlpha = 0.15;
            ctx.lineWidth = 16;
            ctx.stroke(path);

            // Layer 3: Mid glow (30% to 70%)
            ctx.globalAlpha = 0.3;
            ctx.lineWidth = 10;
            ctx.stroke(path);

            // Layer 2: Inner intense glow (70% to 100% dropoff)
            ctx.globalAlpha = 0.7;
            ctx.lineWidth = 5;
            ctx.stroke(path);

            // Layer 1: The solid 1px core thread
            ctx.globalAlpha = 1.0;
            ctx.lineWidth = 1.5; // slight boost from 1px for visibility on retina
            ctx.strokeStyle = '#ffffff'; // The extreme core of magical light is typically white/very bright
            ctx.stroke(path);

            // To maintain the color identity since the core is white, overlay a bright color core
            ctx.strokeStyle = colors[b];
            ctx.globalCompositeOperation = 'source-over'; // switch to normal for core color if we want, but screen is fine
            ctx.stroke(path);

            ctx.globalAlpha = 1.0; // reset for next band
        }

        ctx.restore();
    }

    // RENDER IMPACT
    if (d.phase === 'IMPACT') {
        const blastRadius = tImpact * 150;
        const blastAlpha = 1 - tImpact;

        ctx.save();
        ctx.globalAlpha = blastAlpha;
        ctx.translate(eff.tx, eff.ty);

        // Massive Rainbow Wave
        const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, blastRadius);
        grad.addColorStop(0, 'rgba(255,255,255,1)');
        grad.addColorStop(0.2, 'rgba(0,255,255,0.8)');
        grad.addColorStop(0.5, 'rgba(255,0,255,0.5)');
        grad.addColorStop(1, 'rgba(255,255,0,0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(0, 0, blastRadius, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();

        // Shards
        d.shards.forEach(s => s.draw(ctx));
    }

    ctx.restore();
    return true;
}
